import { Fingerprint, Landmark, Gift, ShieldAlert, Quote } from "lucide-react";

// Plain-English explainer for what an EIN is (guide Step/Mission 2).
export default function EinExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="ein-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Fingerprint className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">What is an EIN?</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        EIN stands for <strong>Employer Identification Number</strong> — a free, 9-digit federal tax ID (formatted like
        {" "}<span className="font-mono">12-3456789</span>) the IRS issues to your business. Think of it as a Social
        Security number for your trucking company.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Landmark className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">What you use it for</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Opening a <strong>business bank account</strong>, filing business taxes, registering with FMCSA for your
            USDOT and MC numbers, and hiring drivers. Almost every setup step asks for it.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">It's free &amp; instant</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Apply directly at <strong>IRS.gov</strong> and you get it in minutes. Never pay a third-party site that
            charges a "filing fee" — the IRS gives you an EIN for free.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          <strong>Two things that trip people up:</strong> using your EIN (not your SSN) on business paperwork keeps your
          personal identity private — and FMCSA won't accept a P.O. box, so pair your EIN with a real commercial or
          registered business address.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Apply for your EIN the moment your LLC is approved, then use it to open your business bank account that same
          week. That order keeps your money separate from day one. This isn't legal or tax advice — confirm with a CPA.
        </p>
      </div>
    </div>
  );
}
